import {
	ActionsDropdown,
	ActionsDropdownItem,
	Anchor,
	Box,
	IBoxComponentProps,
	IconButton,
	IInputCallbackData,
	Input,
	InputContainer,
	InputIcon,
	Image,
	Label,
} from '@moosend/mooskin';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { history } from '../../../history/history';
import { IEmptyPage } from '../EmptyPage';

export const TableRow: React.FC<IBoxComponentProps> = (props) => {
	return (
		<Box borderRadius={4} boxShadow="md" p={8} bgColor="backgroundColors.white" mb={8} {...props}>
			{props.children}
		</Box>
	);
};

export const SearchField: React.FC<{
	value: string;
	onChange: (e: React.ChangeEvent<HTMLInputElement>, data: IInputCallbackData) => void;
	dataLabel?: string;
	id?: string;
	placeholder?: string;
	containerStyle?: { [key: string]: string | number };
}> = ({ value, onChange, dataLabel, id, placeholder, containerStyle }) => {
	return (
		<InputContainer
			borderRadius={2}
			mb={8}
			dataLabel={dataLabel}
			value={value}
			onChangeInput={onChange}
			noRender={['lg', 'md']}
			boxShadow="md"
			{...containerStyle}
		>
			<Input placeholder={placeholder} id={id} />
			<InputIcon>search</InputIcon>
		</InputContainer>
	);
};

export const Filters: React.FC<{
	active: string;
	list: { label: string; value: string }[];
	onClick: (data: IInputCallbackData) => void;
	dataLabel?: string;
}> = ({ active, list, onClick, dataLabel }) => {
	return (
		<Box boxShadow="md" d="flex" borderRadius={3} mb={8} noRender={['lg', 'md']}>
			{list.map((item) => (
				<Box
					d="flex"
					align="center"
					justify="center"
					flex={1}
					minH={28}
					fontSize={12}
					key={item.value}
					fontColor={active === item.value ? 'fontColors.white' : 'fontColors.darkgray1'}
					fontWeight={600}
					onClick={() => onClick({ dataLabel, value: item.value })}
					bgColor={active === item.value ? 'backgroundColors.primary1' : 'backgroundColors.white'}
				>
					{item.label}
				</Box>
			))}
		</Box>
	);
};

export const WizardItem: React.FC<{ url: string; showArrows?: boolean }> = ({ children, url, showArrows }) => {
	return (
		<Link to={url}>
			<Box
				bgColor="backgroundColors.white"
				d="flex"
				justify="space-between"
				p={20}
				borderBottom="1px solid"
				borderBottomColor="borderColors.gray1"
				fontColor="fontColors.black"
				fontSize={16}
				fontWeight={600}
				align="center"
				noRender={['lg', 'md']}
			>
				<Box>{children}</Box>
				{showArrows && <IconButton>chevron_right</IconButton>}
			</Box>
		</Link>
	);
};

export const WizardMenu: React.FC<{ list: { title: string; url: string }[]; showArrows?: boolean }> = ({ list, showArrows }) => {
	return (
		<>
			{list.map((item) => (
				<WizardItem key={item.title} url={item.url} showArrows={showArrows}>
					{item.title}
				</WizardItem>
			))}
		</>
	);
};

let lastScrollTop = 0;
let hideTopBar = false;

export const WizardHeader: React.FC<{
	backUrl?: string;
	boxProps?: IBoxComponentProps;
	actions?: { label: string; onClick?: () => void; href?: string }[];
	label: string;
}> = ({ children, backUrl, boxProps, actions, label }) => {
	const [hideHeader, setHideHeader] = React.useState(false);

	const onScroll = () => {
		let st = window.pageYOffset || document.documentElement.scrollTop;
		if (st > lastScrollTop) {
			if (!hideTopBar) {
				hideTopBar = true;
				setHideHeader(true);
			}
		} else {
			if (hideTopBar) {
				hideTopBar = false;
				setHideHeader(false);
			}
		}
		lastScrollTop = st <= 0 ? 0 : st;
	};

	React.useEffect(() => {
		window.addEventListener('scroll', onScroll);
		return () => {
			window.removeEventListener('scroll', onScroll);
		};
	}, []);

	const [dropdown, setDropdown] = React.useState(false);

	return (
		<Box
			bgColor="backgroundColors.white"
			d="flex"
			borderBottom="1px solid"
			borderBottomColor="borderColors.gray1"
			cursor="pointer"
			p={15}
			fontWeight={600}
			align="center"
			// onClick={onClick}
			noRender={['lg', 'md']}
			fontSize={18}
			position={'fixed'}
			top={hideHeader ? -60 : 50}
			left={0}
			right={0}
			zIndex={3}
			{...boxProps}
		>
			<Box mr="auto" d="flex" align="center" overflow="hidden">
				{backUrl && (
					<Link to={backUrl}>
						<IconButton mr={20} fontSize={28}>
							chevron_left
						</IconButton>
					</Link>
				)}
				<Box whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" flex={1}>
					{label}
				</Box>
			</Box>
			{actions?.length && (
				<>
					<IconButton fontSize={28} onClick={() => setDropdown(!dropdown)}>
						more_vert
					</IconButton>
					<ActionsDropdown isOpen={dropdown} onClose={() => setDropdown(false)}>
						{actions.map((item, itemIndex) => {
							const dropdownItem = (
								<ActionsDropdownItem
									key={itemIndex}
									onClick={() => {
										item.onClick?.();
										setDropdown(false);
									}}
								>
									{item.label}
								</ActionsDropdownItem>
							);
							if (item.href) {
								return (
									<Anchor textDecoration="none" href={item.href} target="_blank">
										{dropdownItem}
									</Anchor>
								);
							}
							return dropdownItem;
						})}
					</ActionsDropdown>
				</>
			)}
			{children}
		</Box>
	);
};

export const MobilePagination: React.FC<{
	totalPages?: number;
	current: number;
	onClick?: (e: React.MouseEvent<HTMLElement>, value: number) => void;
}> = ({ totalPages, current, onClick }) => {
	const onPageClick = (page: number) => {
		onClick && onClick({} as any, page);
	};
	return (
		<Box p={15} d="flex" align="center" justify="space-around" noRender={['lg', 'md']}>
			{totalPages && (
				<IconButton disabled={current === 1} onClick={() => onPageClick(1)}>
					first_page
				</IconButton>
			)}
			<IconButton disabled={current === 1} onClick={() => onPageClick(current - 1)}>
				chevron_left
			</IconButton>
			<Box fontWeight={500}>{`${current}${totalPages ? '/' + totalPages : ''}`}</Box>
			<IconButton disabled={current === totalPages} onClick={() => onPageClick(current + 1)}>
				chevron_right
			</IconButton>
			{totalPages && (
				<IconButton disabled={current === totalPages} onClick={() => onPageClick(totalPages)}>
					last_page
				</IconButton>
			)}
		</Box>
	);
};

export const StepNavMobileComponent: React.FC<{
	items: {
		title: string;
		value: string | number;
	}[];
	onClick: (value: string | number) => void;
	currentIndex: number;
}> = ({ items, onClick, currentIndex }) => {
	const disabledPrev = currentIndex === 0;
	const disabledNext = currentIndex === items.length - 1;
	const [dropdown, setDropdown] = React.useState(false);

	return (
		<Box
			bgColor="backgroundColors.accountNavigation"
			d="flex"
			fontWeight={600}
			align="center"
			direction="row"
			fontColor="fontColors.inverseText"
			fontSize={14}
			justify="space-between"
			p="10px"
			// maxW="360px"
			w="100%"
			borderRadius="30px"
			noRender={['lg', 'md']}
			onClick={() => setDropdown(true)}
		>
			<Box mr="auto" d="flex" align="center" cursor={disabledPrev ? 'not-allowed' : 'pointer'} opacity={disabledPrev ? 0.3 : 1}>
				<Box
					onClick={
						!disabledPrev
							? (e) => {
									e.stopPropagation();
									onClick(items[currentIndex - 1].value);
							  }
							: undefined
					}
				>
					<IconButton ml={0} fontSize={18} fontColor="fontColors.inverseText">
						chevron_left
					</IconButton>
				</Box>
			</Box>
			<Box d="flex" align="center" fontSize={15}>
				{items[currentIndex].title}
			</Box>
			<Box ml="auto" d="flex" align="center" cursor={disabledNext ? 'not-allowed' : 'pointer'} opacity={disabledNext ? 0.3 : 1}>
				<Box
					onClick={
						!disabledNext
							? (e) => {
									e.stopPropagation();
									onClick(items[currentIndex + 1].value);
							  }
							: undefined
					}
				>
					<IconButton mr={0} fontSize={18} fontColor="fontColors.inverseText">
						chevron_right
					</IconButton>
				</Box>
			</Box>
			<ActionsDropdown
				isOpen={dropdown}
				onClose={(e) => {
					e.stopPropagation();
					setDropdown(false);
				}}
			>
				{items.map((item, i) => {
					return (
						<ActionsDropdownItem
							onClick={(e) => {
								e.stopPropagation();
								onClick(items[i].value);
								setDropdown(false);
							}}
						>
							{item.title}
						</ActionsDropdownItem>
					);
				})}
			</ActionsDropdown>
		</Box>
	);
};

export const MobileExpander: React.FC<{
	shouldShowExpand?: boolean;
	areStatisticsVisible?: boolean;
	onClick?: (e: React.MouseEvent<HTMLElement>) => void;
	fontSize?: number;
	pt?: number;
}> = ({ shouldShowExpand, areStatisticsVisible, onClick, pt, fontSize }) => {
	const onClickExpander = () => {
		onClick && onClick({} as any);
	};
	return (
		<Box d="flex" justify="center">
			{shouldShowExpand && (
				<Box w="auto" d="flex" fontSize={fontSize} cursor="pointer" pt={pt} onClick={onClickExpander}>
					<IconButton>{areStatisticsVisible ? 'expand_less' : 'expand_more'}</IconButton>
				</Box>
			)}
		</Box>
	);
};

export const MobileEmptyPage: React.FC<IEmptyPage> = (props) => {
	return (
		<Box p={10} className={props.className} style={props.style} noRender={['lg', 'md']}>
			<Image w="100%" maxW={400} src={props.src} />
			<Label d="block" my={30} fontColor="fontColors.medgray1" textAlign="center">
				{props.title}
			</Label>
		</Box>
	);
};

export const MessageForDesktop: React.FC = (props) => (
	<Box noRender={['lg', 'md']} p={20} bgColor="backgroundColors.primary5" fontWeight={500} fontSize={14}>
		{props.children}
	</Box>
);
